class ElementStateToggle {
    constructor(options = {}) {
        const defaultOptions = {
            triggerElementSelector: '.js-state-trigger',
            preventDefault: false,
            hasContextElement: {
                elements: null,
                attr: 'href', // Could be data as alternative - Destination where it should point to
                name: '', // Use this only if the attribute is a dataset
            },
            scrollToTrigger: false,
            animate: false,
            animationSpeed: 2000,
            removeAllActiveClasses: true,
            toggleActiveElement: false,
            stateClasses: {
                active: 'active',
            },
            offset: 0,
            callback: null,
            hooks: {
                afterToggle: null,
            },
        };

        this.options = {...defaultOptions, ...options};

        this.triggerElements = document.querySelectorAll(this.options.triggerElementSelector);

        if (this.options.hasContextElement.elements) {
            this.contextElement = document.querySelectorAll(this.options.hasContextElement.elements);
        }
    }

    static toArray(list) {
        return Array.from(list);
    }

    static scrollToElement(target = null, offset = 0) {
        if (!target) return;

        const y = target.getBoundingClientRect().top + window.pageYOffset + offset;
        window.scrollTo({top: y, behavior: 'smooth'});
    }

    init() {
        this.triggerElements = ElementStateToggle.toArray(this.triggerElements);

        if (this.options.animate) {
            this.animateElements();
        } else {
            this.registerEventListener({
                callback: (this.options.callback) ? this.options.callback.bind(this) : this.toggleState.bind(this),
            });
        }
    }

    /**
     * registerEventListener
     *
     * Wrapper for adding a specific event to multiple elements.
     *
     * @param eventType {String} Type of event as string (e.g. click, mouseover, mouseenter, etc.)
     * @param elements {NodeList} List of elements
     * @param callback {Function|null} Callback which should be triggered after the event was fired
     */
    registerEventListener({eventType = 'click', elements = this.triggerElements, callback = null}) {
        elements.forEach((element) => {
            element.addEventListener(eventType, (event) => {
                if (this.options.preventDefault) {
                    event.preventDefault();
                }

                callback(event);
            });
        });
    }

    animateElements() {
        const elements = this.triggerElements;
        let index = 0;

        if (!elements.length) return;

        setInterval(() => {
            const target = elements[index];

            this.removeActiveClasses(this.triggerElements);
            this.addActiveClass(target);

            index += 1;

            if (index > (elements.length - 1)) index = 0;
        }, this.options.animationSpeed);
    }

    /**
     * toggleState
     *
     * Toggles an elements state by removing and adding CSS-Classes
     *
     * @param event {Event} Default JS event object
     */
    toggleState(event) {
        const target = event.currentTarget;
        const isActive = target.classList.contains(this.options.stateClasses.active);

        if (this.options.removeAllActiveClasses) {
            this.removeActiveClasses();
        } else {
            this.removeActiveClass(target);
        }

        if (!isActive || !this.options.toggleActiveElement) {
            this.addActiveClass(target);
        }

        if (this.options.scrollToTrigger) {
            event.preventDefault();
            ElementStateToggle.scrollToElement(target, this.options.offset);
        }

        this.setContextElementStatus(event);

        this.afterToggleState(event);
    }

    setContextElementStatus(event) {
        const contextValue = this.getContextPropertyValue(event.currentTarget);
        const context = document.querySelector(contextValue);

        if (context && this.contextElement.length) {
            this.removeActiveClasses(this.contextElement);
            this.addActiveClass(context);
        }
    }

    getContextPropertyValue(target) {
        let property = null;

        if (this.options.hasContextElement.attr === 'data') {
            property = target.dataset[this.options.hasContextElement.name];
        } else {
            property = target.getAttribute(this.options.hasContextElement.attr);
        }

        return property;
    }

    /**
     * removeActiveClasses
     *
     * Removes the configured active class from all elements. Default: active
     */
    removeActiveClasses(elements = this.triggerElements) {
        elements.forEach(element => element.classList.remove(this.options.stateClasses.active));
    }

    /**
     * addActiveClass
     *
     * Add the condifgured active class. Default: active
     *
     * @param target {EventTarget|HTMLElement} The clicked element
     */
    removeActiveClass(target) {
        target.classList.remove(this.options.stateClasses.active);
    }

    /**
     * addActiveClass
     *
     * Add the condifgured active class. Default: active
     *
     * @param target {EventTarget|HTMLElement} The clicked element
     */
    addActiveClass(target) {
        target.classList.add(this.options.stateClasses.active);
    }

    /**
     * afterToggleState
     *
     * Event hook to trigger a callback after toggling has completed.
     *
     * @param event
     */
    afterToggleState(event) {
        if (this.options.hooks && typeof this.options.hooks.afterToggle === 'function') {
            this.options.hooks.afterToggle(event);
        }
    }
}

const elementStateToggle = {
    init(options) {
        this.elementStateToggle = new ElementStateToggle(options);
        this.elementStateToggle.init();
    },
};

export default elementStateToggle;
